import React, { useState, useEffect, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ApiURL from '../Api/Api';
import CreateTransactionReport from './CreateTransactionReport';
import { useNavigate } from 'react-router-dom';
import { SidebarContext } from './SidebarContext';
import Sidebar from './Sidebar';
import Navbar from './Navbar';
import TransactionImageField from './TransactionImageField';
import { fetchTransactionPhotoRequired, imagePayload } from '../utils/transactionPhoto';
import {
  ArrowPathIcon,
  ExclamationTriangleIcon,
  WalletIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  CurrencyRupeeIcon,
  CalendarIcon,
  HashtagIcon,
  DocumentTextIcon,
  TagIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  UserIcon,
  ClipboardDocumentListIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';

/* ─── tiny design tokens ──────────────────────────────────────────────── */
const colors = {
  debit: { bg: '#ef4444', light: '#fef2f2', ring: 'ring-red-400', text: 'text-red-600', border: 'border-red-400' },
  credit: { bg: '#10b981', light: '#ecfdf5', ring: 'ring-emerald-400', text: 'text-emerald-600', border: 'border-emerald-400' },
};

/* ─── reusable input wrapper ──────────────────────────────────────────── */
const Field = ({ label, required, icon: Icon, children }) => (
  <div className="flex flex-col gap-1">
    <label className="text-[10px] font-bold tracking-widest text-slate-700 uppercase select-none">
      {label}{required && <span className="text-rose-400 ml-0.5">*</span>}
    </label>
    <div className="relative">
      {Icon && (
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
          <Icon className="w-[15px] h-[15px]" />
        </span>
      )}
      <div className={Icon ? 'pl-9' : ''}>{children}</div>
    </div>
  </div>
);

const inputCls =
  'w-full h-10 px-3 text-[13px] font-medium text-slate-700 bg-white border border-slate-200 rounded-lg outline-none transition-all focus:border-brand-500 focus:ring-2 focus:ring-brand-100 placeholder:text-slate-300';

const selectCls = inputCls + ' appearance-none pr-8 cursor-pointer';

/* ─── Select with chevron ─────────────────────────────────────────────── */
const Select = ({ children, ...props }) => (
  <div className="relative">
    <select className={selectCls} {...props}>{children}</select>
    <ChevronDownIcon className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
  </div>
);

/* ═══════════════════════════════════════════════════════════════════════ */
const TransactionForm = () => {
  const [formData, setFormData] = useState({
    transactionNo: '',
    transactionDate: new Date().toISOString().split('T')[0],
    amountType: 'debit',
    creditAmount: '',
    debitAmount: '',
    givenReceived: '',
    givenReceivedType: 'non-worker',
    site: '',
    useCustomSite: false,
    projectName: '',
    mainHead: '',
    subHead: '',
    fromDestination: '',
    toDestination: '',
    travelMode: '',
    remarks: '',
  });

  // ── CHANGE 1: track whether site is locked due to worker/admin selection ──
  const [siteLocked, setSiteLocked] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mainHeads, setMainHeads] = useState([]);
  const [allAttributes, setAllAttributes] = useState([]);
  const [subHeads, setSubHeads] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [sites, setSites] = useState([]);
  const [userBalance, setUserBalance] = useState(0);
  const [recipientBalances, setRecipientBalances] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [userRole, setUserRole] = useState('');
  const [companyId, setCompanyId] = useState(localStorage.getItem('company_id') || '');
  const [assignedSites, setAssignedSites] = useState([]);
  const [projects, setProjects] = useState([]);
  const [fieldSettings, setFieldSettings] = useState({
    travel_fields_enabled: true,
    travel_fields_required: false,
    remarks_required_for_other: true,
  });
  const [transactionImage, setTransactionImage] = useState(null);
  const [photoRequired, setPhotoRequired] = useState(false);

  const navigate = useNavigate();
  const user_id = localStorage.getItem('id');
  const user_name = localStorage.getItem('user_name') || 'Unknown';
  const { isSidebarOpen } = useContext(SidebarContext);

  const isDebit = formData.amountType === 'debit';
  const accent = isDebit ? colors.debit : colors.credit;
  const amountValue = isDebit ? formData.debitAmount : formData.creditAmount;

  // ── filtered sub heads: only show sub heads belonging to selected main head ──
  const filteredSubHeads = formData.mainHead
    ? [...new Set(allAttributes.filter(a => a.main_head_name === formData.mainHead).map(a => a.sub_main_name).filter(Boolean))]
    : subHeads;
  const projectOptions = [...new Set(projects.map((project) => project.project_name).filter(Boolean))].sort();
  const selectedProjectRows = projects.filter((project) => project.project_name === formData.projectName);
  const selectedProjectHeadRows = selectedProjectRows.filter((project) => project.main_head_name || project.sub_main_name);
  const hasProjectSpecificHeads = selectedProjectHeadRows.length > 0;
  const projectHeads = hasProjectSpecificHeads
    ? [...new Set(selectedProjectHeadRows.map((project) => project.main_head_name).filter(Boolean))].sort()
    : mainHeads;
  const projectSubHeads = hasProjectSpecificHeads
    ? [...new Set(
        selectedProjectHeadRows
          .filter((project) => !formData.mainHead || project.main_head_name === formData.mainHead)
          .map((project) => project.sub_main_name)
          .filter(Boolean)
      )].sort()
    : filteredSubHeads;
  const isTravelHead = fieldSettings.travel_fields_enabled && String(formData.mainHead || '').trim().toLowerCase() === 'travel';
  const isOtherHead = ['other', 'others', 'miscellaneous', 'misc'].includes(String(formData.mainHead || '').trim().toLowerCase());
  const travelModes = ['Car', 'Metro', 'Bus', 'Train', 'Flight', 'Bike', 'Auto', 'Taxi', 'Other'];

  useEffect(() => {
    fetchData();
    fetchUserRole();
    fetchTransactionPhotoRequired(user_id).then(setPhotoRequired).catch(() => {});
  }, []);

  const fetchUserRole = async () => {
    try {
      const res = await fetch(`${ApiURL}/getUserRole.php`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id }),
      });
      const data = await res.json();
      if (data.status === 'success') {
        setUserRole(data.role);
        setCompanyId(data.company_id || '');
        setAssignedSites(data.assigned_sites || []);
      }
    } catch (e) { console.error(e); }
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const res = await fetch(`${ApiURL}/getTransactionForm.php`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id }),
      });
      const data = await res.json();
      if (data.status === 'success') {
        setMainHeads(data.mainHeads || []);
        setAllAttributes(data.attributes || []);
        setSubHeads([...new Set(data.attributes?.map(a => a.sub_main_name).filter(Boolean) || [])]);
        setRecipients(data.recipients || data.workers || []);
        setSites(data.sites || []);
        setProjects(data.projects || []);
        setFieldSettings(data.field_settings || fieldSettings);
        setUserBalance(data.user_balance || 0);
        setRecipientBalances(data.worker_balances || {});
        if (data.role) setUserRole(data.role);
        if (data.company_id) setCompanyId(data.company_id);
        if (Array.isArray(data.assigned_sites)) setAssignedSites(data.assigned_sites);
      }
    } catch (e) {
      toast.error(`Network error: ${e.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // ── CHANGE 2: when amountType switches to credit → force party type + pre-fill name ──
    if (name === 'amountType') {
      if (value === 'credit') {
        setFormData(prev => ({
          ...prev,
          amountType: 'credit',
          creditAmount: '',
          debitAmount: '',
          givenReceivedType: 'non-worker',
          givenReceived: 'Company Funds',
        }));
        setSiteLocked(false);
        return;
      } else {
        // switching back to debit → clear the auto-fill
        setFormData(prev => ({
          ...prev,
          amountType: 'debit',
          creditAmount: '',
          debitAmount: '',
          givenReceived: '',
        }));
        setSiteLocked(false);
        return;
      }
    }

    // ── CHANGE 3: when a worker/admin is selected → use shared sites and only lock if there is one ──
    if (name === 'givenReceived' && (formData.givenReceivedType === 'worker' || formData.givenReceivedType === 'admin')) {
      const selected = recipients.find(r => r.user_name === value);
      const recipientSites = selected?.assigned_sites || [];
      const sharedSites = userRole === 'company_owner'
        ? recipientSites
        : recipientSites.filter(siteName => assignedSites.includes(siteName));
      const nextSite = sharedSites[0] || recipientSites[0] || '';
      const shouldLockSite = sharedSites.length === 1;
      setSiteLocked(shouldLockSite);
      setFormData(prev => ({
        ...prev,
        givenReceived: value,
        site: shouldLockSite
          ? nextSite
          : (sharedSites.includes(prev.site) ? prev.site : nextSite),
        useCustomSite: false,
      }));
      return;
    }

    // ── CHANGE 4: when party type changes → unlock site, clear givenReceived ──
    if (name === 'givenReceivedType') {
      setSiteLocked(false);
      setFormData(prev => ({
        ...prev,
        givenReceivedType: value,
        givenReceived: '',
      }));
      return;
    }

    // default handler (unchanged)
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
      ...(name === 'useCustomSite' && { site: checked ? '' : (sites[0]?.site_name || '') }),
      ...(name === 'mainHead' && {
        subHead: '',
        ...(String(value || '').trim().toLowerCase() !== 'travel'
          ? { fromDestination: '', toDestination: '', travelMode: '' }
          : {}),
      }),
    }));
  };

  useEffect(() => {
    if (!formData.useCustomSite && sites.length > 0 && !sites.some(s => s.site_name === formData.site)) {
      setFormData(p => ({ ...p, site: sites[0]?.site_name || '' }));
    }
  }, [sites]);

  useEffect(() => {
    if (userRole && userRole !== 'company_owner' && formData.amountType === 'credit') {
      setFormData(p => ({ ...p, amountType: 'debit', creditAmount: '' }));
    }
  }, [userRole]);

  const recipientOptions = recipients.filter(r => {
    if (formData.givenReceivedType === 'worker') return r.role === 'worker';
    if (formData.givenReceivedType === 'admin') return r.role === 'admin';
    return true;
  });

  const selectedRecipient = recipients.find(r => r.user_name === formData.givenReceived);
  const selectedRecipientSites = selectedRecipient?.assigned_sites || [];
  const allowedSiteNames = (formData.givenReceivedType === 'worker' || formData.givenReceivedType === 'admin')
    ? (userRole === 'company_owner'
        ? selectedRecipientSites
        : selectedRecipientSites.filter(siteName => assignedSites.includes(siteName)))
    : [];
  const hasScopedRecipient = formData.givenReceivedType === 'worker' || formData.givenReceivedType === 'admin';
  const siteOptions = hasScopedRecipient
    ? sites.filter(site => allowedSiteNames.includes(site.site_name))
    : sites;
  const hasSiteOptions = siteOptions.length > 0;

  const formatCurrency = (n) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 }).format(n);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setIsSubmitting(true);
    const amount = parseFloat(amountValue);
    if (!Number.isFinite(amount) || amount <= 0) {
      setIsSubmitting(false);
      toast.error('Amount is required and must be greater than zero');
      return;
    }
    if (!String(formData.site || '').trim()) {
      setIsSubmitting(false);
      toast.error('Please select an assigned site before saving');
      return;
    }
    if (isDebit) {
      if (amount > userBalance) {
        setIsSubmitting(false);
        toast.error(`Insufficient balance! Available: ${formatCurrency(userBalance)}`);
        return;
      }
    }
    if (photoRequired && !transactionImage?.dataUrl) {
      setIsSubmitting(false);
      toast.error('Transaction photo is required by company settings');
      return;
    }
    if (isOtherHead && fieldSettings.remarks_required_for_other && !String(formData.remarks || '').trim()) {
      setIsSubmitting(false);
      toast.error('Remarks are required for Other/Miscellaneous');
      return;
    }
    const payload = {
      ...formData,
      creditAmount: !isDebit ? formData.creditAmount : '',
      debitAmount: isDebit ? formData.debitAmount : '',
      user_id, created_by: user_name, company_id: companyId || null,
      attachment_image: imagePayload(transactionImage),
    };
    try {
      const res = await fetch(`${ApiURL}/txnSaveModify.php`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const result = await res.json();
      if (result.status === 'success') {
        toast.success('Transaction saved successfully!');
        if (Array.isArray(result.warnings) && result.warnings.length) {
          toast.warning(result.warnings.join('\n'));
        }
        setFormData({
          transactionNo: '', transactionDate: new Date().toISOString().split('T')[0],
          amountType: 'debit', creditAmount: '', debitAmount: '', givenReceived: '',
          givenReceivedType: 'non-worker', site: sites[0]?.site_name || '',
          useCustomSite: false, projectName: '', mainHead: '', subHead: '', fromDestination: '', toDestination: '', travelMode: '', remarks: '',
        });
        setSiteLocked(false);
        setTransactionImage(null);
        setRefreshTrigger(p => p + 1);
        fetchData();
      } else throw new Error(result.message || 'Failed to save');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ─── Role badge ──────────────────────────────────────────────────── */
  const roleBadge = {
    company_owner: { label: 'Owner', cls: 'bg-amber-100 text-amber-700' },
    admin:         { label: 'Admin', cls: 'bg-brand-100 text-brand-700' },
    worker:        { label: 'Worker', cls: 'bg-slate-100 text-slate-600' },
  }[userRole] || { label: userRole, cls: 'bg-slate-100 text-slate-600' };

  // ── CHANGE 5: credit mode → only show "Other" option, disabled ──
  const isCredit = !isDebit;
  const partyTypeOptions = isCredit
    ? [{ value: 'non-worker', label: 'Other' }]
    : [
        ...(userRole === 'company_owner' ? [{ value: 'admin', label: 'Admin' }] : []),
        { value: 'worker', label: 'Worker' },
        { value: 'non-worker', label: 'Other' },
      ];

  const handleProjectChange = (value) => {
    const projectRows = projects
      .filter((item) => item.project_name === value)
      .filter((item) => item.main_head_name || item.sub_main_name);
    const firstHead = projectRows.length === 1 ? (projectRows[0].main_head_name || '') : '';
    const firstSubHead = projectRows.length === 1 ? (projectRows[0].sub_main_name || '') : '';
    setFormData((prev) => ({
      ...prev,
      projectName: value,
      mainHead: firstHead,
      subHead: firstSubHead,
    }));
  };

  return (
    <div className="app-shell min-h-screen">
      <Navbar />
      <div className="flex pt-16">
        <Sidebar />
        <main className={`flex-1 transition-all duration-300 w-full min-w-0 ${isSidebarOpen ? 'lg:ml-72' : ''}`}>
          <div className="w-full px-3 py-4 sm:px-5 sm:py-5 lg:px-6 lg:py-6">

            {/* ── Page header ────────────────────────────────────────── */}
            <div className="page-surface flex items-center justify-between gap-3 mb-5 p-4 sm:p-5">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-brand-100 flex items-center justify-center">
                  <ClipboardDocumentListIcon className="w-4 h-4 sm:w-5 sm:h-5 text-brand-700" />
                </div>
                <div>
                  <h1 className="text-base sm:text-lg font-bold text-slate-800 leading-tight">Transaction Entry</h1>
                  <p className="text-[10px] sm:text-[11px] text-slate-500">Record payments & fund receipts</p>
                </div>
              </div>
              {/* Balance pill – desktop */}
              <div className="hidden sm:flex items-center gap-3 bg-white border border-slate-200 rounded-lg px-4 py-2.5 shadow-sm">
                <WalletIcon className="w-4 h-4 text-slate-500" />
                <div>
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider font-bold">Balance</p>
                  <p className="text-[15px] font-bold text-slate-800">{formatCurrency(userBalance)}</p>
                </div>
                <span className={`ml-2 text-[10px] font-bold px-2 py-0.5 rounded-full ${roleBadge.cls}`}>
                  {roleBadge.label}
                </span>
              </div>
            </div>

            {/* Balance bar – mobile only */}
            <div className="sm:hidden flex items-center justify-between bg-white border border-slate-200 rounded-lg px-4 py-3 mb-4 shadow-sm">
              <div className="flex items-center gap-2">
                <WalletIcon className="w-4 h-4 text-slate-500" />
                <p className="text-[11px] text-slate-500 uppercase font-bold tracking-wider">Balance</p>
              </div>
              <div className="flex items-center gap-2">
                <p className="text-base font-bold text-slate-800">{formatCurrency(userBalance)}</p>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${roleBadge.cls}`}>{roleBadge.label}</span>
              </div>
            </div>

            {/* ── Main card - Full width ──────────────────────────────── */}
            <div className="pro-card overflow-hidden w-full">

              {/* Type toggle header */}
              <div className="flex border-b border-slate-200">
                {[
                  { type: 'debit',  label: 'Debit',  sub: 'Payment Out',    icon: ArrowDownTrayIcon, color: 'text-red-600',     active: 'border-b-2 border-red-500 bg-red-50/60' },
                  { type: 'credit', label: 'Credit', sub: 'Fund Received',  icon: ArrowUpTrayIcon,   color: 'text-emerald-600', active: 'border-b-2 border-emerald-500 bg-emerald-50/60', disabled: userRole !== 'company_owner' },
                ].map(({ type, label, sub, icon: Icon, color, active, disabled }) => (
                  <button
                    key={type}
                    type="button"
                    disabled={disabled}
                    onClick={() => !disabled && setFormData(p => ({ ...p, amountType: type, debitAmount: '', creditAmount: '' }))}
                    className={`flex-1 flex items-center justify-center gap-1.5 sm:gap-2.5 py-3 sm:py-3.5 text-xs sm:text-sm font-semibold transition-all
                      ${formData.amountType === type ? active : 'text-slate-500 hover:bg-slate-50'}
                      ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}
                    `}
                  >
                    <Icon className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${formData.amountType === type ? color : ''}`} />
                    <span className={formData.amountType === type ? color : ''}>
                      {label}
                      <span className="hidden md:inline font-normal text-xs ml-1 opacity-60">- {sub}</span>
                    </span>
                  </button>
                ))}
              </div>

              {/* Form body */}
              {isLoading ? (
                <div className="flex flex-col items-center justify-center py-16 gap-3">
                  <ArrowPathIcon className="w-7 h-7 text-brand-500 animate-spin" />
                  <p className="text-sm text-slate-400">Loading form...</p>
                </div>
              ) : error ? (
                <div className="flex flex-col items-center justify-center py-16 gap-3">
                  <ExclamationTriangleIcon className="w-7 h-7 text-amber-400" />
                  <p className="text-sm text-slate-500">{error}</p>
                  <button onClick={fetchData} className="text-sm text-brand-600 font-medium">Retry</button>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="p-4 sm:p-6">

                    {/* ── ROW 1: 4 columns ─────────────────────── */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-4">
                      {/* Txn No */}
                      <Field label="Txn No" icon={HashtagIcon}>
                        <input
                          type="text" value={formData.transactionNo} readOnly
                          placeholder="Auto" className={inputCls + ' cursor-default text-slate-400'}
                        />
                      </Field>

                      {/* Date */}
                      <Field label="Date" icon={CalendarIcon} required>
                        <input type="date" name="transactionDate" value={formData.transactionDate}
                          onChange={handleChange} className={inputCls} />
                      </Field>

                      {/* Amount */}
                      <Field label={isDebit ? 'Debit Amount' : 'Credit Amount'} icon={CurrencyRupeeIcon} required>
                        <input
                          type="number"
                          name={isDebit ? 'debitAmount' : 'creditAmount'}
                          value={isDebit ? formData.debitAmount : formData.creditAmount}
                          onChange={handleChange}
                          className={`${inputCls} ${isDebit
                            ? 'focus:border-red-400 focus:ring-red-100'
                            : 'focus:border-emerald-400 focus:ring-emerald-100'
                          }`}
                          placeholder="0.00" step="0.01" min="0"
                        />
                      </Field>

                      {/* Created By */}
                      <Field label="Created By" icon={UserIcon}>
                        <input type="text" value={user_name} readOnly
                          className={inputCls + ' cursor-default text-slate-400'} />
                      </Field>
                    </div>

                    {/* ── ROW 2 ── */}
                    <div className="grid grid-cols-1 sm:grid-cols-6 gap-3 sm:gap-4 mb-6">

                      {/* ── MOBILE ROW: Party Type + Site in one row ── */}
                      {/* Desktop: Party Type (same as before) */}
                      {(userRole === 'admin' || userRole === 'company_owner') && (
                        <>
                          {/* Desktop: Button group */}
                          <div className="hidden sm:block sm:col-span-1">
                            <Field label="Party Type" icon={UserGroupIcon}>
                              <div className="h-10 flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-lg px-2">
                                {partyTypeOptions.map(({ value, label }) => (
                                  <button
                                    key={value}
                                    type="button"
                                    disabled={isCredit}
                                    onClick={() => !isCredit && setFormData(p => ({ ...p, givenReceivedType: value, givenReceived: '' }))}
                                    className={`flex-1 text-[11px] font-semibold h-6 rounded-md transition-all ${
                                      formData.givenReceivedType === value
                                        ? 'bg-brand-600 text-white shadow-sm'
                                        : 'text-slate-600 hover:text-slate-800'
                                    } ${isCredit ? 'cursor-not-allowed opacity-70' : ''}`}
                                  >
                                    {label}
                                  </button>
                                ))}
                              </div>
                            </Field>
                          </div>

                          {/* Mobile: Party Type + Site in same row using a sub-grid */}
                          <div className="sm:hidden col-span-1 grid grid-cols-2 gap-3">
                            {/* Party Type dropdown */}
                            <Field label="Party Type" icon={UserGroupIcon}>
                              <Select
                                value={formData.givenReceivedType}
                                disabled={isCredit}
                                onChange={(e) => setFormData(p => ({ ...p, givenReceivedType: e.target.value, givenReceived: '' }))}
                              >
                                {partyTypeOptions.map(({ value, label }) => (
                                  <option key={value} value={value}>{label}</option>
                                ))}
                              </Select>
                            </Field>

                            {/* Site / Location (mobile, inside this row) */}
                            <Field label="Site / Location" icon={BuildingOfficeIcon} required>
                              {formData.useCustomSite && !siteLocked ? (
                                <input type="text" name="site" value={formData.site}
                                  onChange={handleChange} className={inputCls} placeholder="Custom site" />
                              ) : (
                                <Select
                                  name="site"
                                  value={formData.site}
                                  onChange={handleChange}
                                  disabled={siteLocked}
                                >
                                  {siteLocked
                                    ? <option value={formData.site}>{formData.site}</option>
                                    : !hasSiteOptions
                                      ? <option value="">No assigned sites</option>
                                    : siteOptions.map(s => (
                                        <option key={s.site_id} value={s.site_name}>{s.site_name}</option>
                                      ))
                                  }
                                </Select>
                              )}
                              {!siteLocked && (
                                <label className="flex items-center gap-1.5 mt-1 cursor-pointer w-fit">
                                  <input type="checkbox" name="useCustomSite" checked={formData.useCustomSite}
                                    onChange={handleChange}
                                    className="w-3 h-3 rounded border-slate-300 text-brand-600 focus:ring-brand-500" />
                                  <span className="text-[10px] text-slate-500">Custom site</span>
                                </label>
                              )}
                            </Field>
                          </div>
                        </>
                      )}

                      {/* Given / Received */}
                      <div className={`${(userRole === 'admin' || userRole === 'company_owner') ? 'sm:col-span-2' : 'sm:col-span-3'}`}>
                        <Field
                          label={isDebit ? 'Given To' : 'Received From'}
                          icon={UserIcon} required
                        >
                          {(formData.givenReceivedType === 'worker' || formData.givenReceivedType === 'admin') && !isCredit ? (
                            <Select name="givenReceived" value={formData.givenReceived} onChange={handleChange}>
                              <option value="">Select {formData.givenReceivedType}</option>
                              {recipientOptions.map(r => (
                                <option key={r.id} value={r.user_name}>
                                  {r.user_name} - {formatCurrency(recipientBalances[r.user_name] || 0)}
                                </option>
                              ))}
                            </Select>
                          ) : (
                            // credit → "Company Funds" pre-filled but still editable
                            <input
                              type="text"
                              name="givenReceived"
                              value={formData.givenReceived}
                              onChange={handleChange}
                              className={inputCls}
                              placeholder="Name / business"
                            />
                          )}
                        </Field>
                      </div>

                      {/* Site – desktop (unchanged) */}
                      <div className="hidden sm:block sm:col-span-1">
                        <Field label="Site / Location" icon={BuildingOfficeIcon} required>
                          {formData.useCustomSite && !siteLocked ? (
                            <input type="text" name="site" value={formData.site}
                              onChange={handleChange} className={inputCls} placeholder="Custom site" />
                          ) : (
                            <Select
                              name="site"
                              value={formData.site}
                              onChange={handleChange}
                              disabled={siteLocked}
                            >
                              {siteLocked
                                ? <option value={formData.site}>{formData.site}</option>
                                : !hasSiteOptions
                                  ? <option value="">No assigned sites</option>
                                : siteOptions.map(s => (
                                    <option key={s.site_id} value={s.site_name}>{s.site_name}</option>
                                  ))
                              }
                            </Select>
                          )}
                          {!siteLocked && (
                            <label className="flex items-center gap-1.5 mt-1 cursor-pointer w-fit">
                              <input type="checkbox" name="useCustomSite" checked={formData.useCustomSite}
                                onChange={handleChange}
                                className="w-3 h-3 rounded border-slate-300 text-brand-600 focus:ring-brand-500" />
                              <span className="text-[10px] text-slate-500">Custom site</span>
                            </label>
                          )}
                        </Field>
                      </div>

                      {/* ── MOBILE: Main Head + Sub Head in one row ── */}
                      {/* Desktop: Main Head */}
                      <div className="hidden sm:block sm:col-span-1">
                        <Field label="Main Head" icon={TagIcon}>
                          <Select name="mainHead" value={formData.mainHead} onChange={handleChange}>
                            <option value="">Select</option>
                            {projectHeads.map(h => <option key={h} value={h}>{h}</option>)}
                          </Select>
                        </Field>
                      </div>

                      {/* Desktop: Sub Head */}
                      <div className="hidden sm:block sm:col-span-1">
                        <Field label="Sub Head" icon={TagIcon}>
                          <Select name="subHead" value={formData.subHead} onChange={handleChange}>
                            <option value="">Select</option>
                            {projectSubHeads.map(s => <option key={s} value={s}>{s}</option>)}
                          </Select>
                        </Field>
                      </div>

                      {/* Mobile: Main Head + Sub Head in one row */}
                      <div className="sm:hidden col-span-1 grid grid-cols-2 gap-3">
                        <Field label="Main Head" icon={TagIcon}>
                          <Select name="mainHead" value={formData.mainHead} onChange={handleChange}>
                            <option value="">Select</option>
                            {projectHeads.map(h => <option key={h} value={h}>{h}</option>)}
                          </Select>
                        </Field>
                        <Field label="Sub Head" icon={TagIcon}>
                          <Select name="subHead" value={formData.subHead} onChange={handleChange}>
                            <option value="">Select</option>
                            {projectSubHeads.map(s => <option key={s} value={s}>{s}</option>)}
                          </Select>
                        </Field>
                      </div>

                      <div className="sm:col-span-1">
                        <Field label="Project" icon={BuildingOfficeIcon}>
                          <Select name="projectName" value={formData.projectName} onChange={(e) => handleProjectChange(e.target.value)}>
                            <option value="">No Project</option>
                            {projectOptions.map((projectName) => (
                              <option key={projectName} value={projectName}>{projectName}</option>
                            ))}
                          </Select>
                        </Field>
                      </div>

                      {isTravelHead && (
                        <div className="sm:col-span-6 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 rounded-lg border border-brand-100 bg-brand-50/50 p-3">
                          <Field label="From Destination" icon={BuildingOfficeIcon}>
                            <input
                              type="text"
                              name="fromDestination"
                              value={formData.fromDestination}
                              onChange={handleChange}
                              className={inputCls}
                              placeholder="Starting point"
                            />
                          </Field>
                          <Field label="To Destination" icon={BuildingOfficeIcon}>
                            <input
                              type="text"
                              name="toDestination"
                              value={formData.toDestination}
                              onChange={handleChange}
                              className={inputCls}
                              placeholder="Destination"
                            />
                          </Field>
                          <Field label="Travel Mode" icon={TagIcon}>
                            <Select name="travelMode" value={formData.travelMode} onChange={handleChange}>
                              <option value="">Select mode</option>
                              {travelModes.map((mode) => (
                                <option key={mode} value={mode}>{mode}</option>
                              ))}
                            </Select>
                          </Field>
                        </div>
                      )}

                      {/* Remarks */}
                      <div className="sm:col-span-1">
                        <Field label="Remarks" icon={DocumentTextIcon} required={isOtherHead && fieldSettings.remarks_required_for_other}>
                          <input type="text" name="remarks" value={formData.remarks}
                            onChange={handleChange} className={inputCls}
                            placeholder="Optional note..." />
                        </Field>
                      </div>

                      {/* Mobile: Site (for worker role — no Party Type row) */}
                      {!(userRole === 'admin' || userRole === 'company_owner') && (
                        <div className="sm:hidden col-span-1">
                          <Field label="Site / Location" icon={BuildingOfficeIcon} required>
                            {formData.useCustomSite && !siteLocked ? (
                              <input type="text" name="site" value={formData.site}
                                onChange={handleChange} className={inputCls} placeholder="Custom site" />
                            ) : (
                              <Select
                                name="site"
                                value={formData.site}
                                onChange={handleChange}
                                disabled={siteLocked}
                              >
                                {siteLocked
                                  ? <option value={formData.site}>{formData.site}</option>
                                  : !hasSiteOptions
                                    ? <option value="">No assigned sites</option>
                                  : siteOptions.map(s => (
                                      <option key={s.site_id} value={s.site_name}>{s.site_name}</option>
                                    ))
                                }
                              </Select>
                            )}
                            {!siteLocked && (
                              <label className="flex items-center gap-1.5 mt-1 cursor-pointer w-fit">
                                <input type="checkbox" name="useCustomSite" checked={formData.useCustomSite}
                                  onChange={handleChange}
                                  className="w-3 h-3 rounded border-slate-300 text-brand-600 focus:ring-brand-500" />
                                <span className="text-[10px] text-slate-500">Custom site</span>
                              </label>
                            )}
                          </Field>
                        </div>
                      )}
                    </div>

                    {/* ── Submit ─────────────────────────────────────── */}
                    <div className="mb-5">
                      <TransactionImageField
                        value={transactionImage}
                        onChange={setTransactionImage}
                        required={photoRequired}
                        disabled={isSubmitting}
                      />
                    </div>

                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 pt-4 border-t border-slate-100">
                      <motion.button
                        type="submit"
                        disabled={isSubmitting || !hasSiteOptions}
                        whileTap={{ scale: 0.98 }}
                        style={{ background: isDebit
                          ? 'linear-gradient(135deg,#ef4444,#dc2626)'
                          : 'linear-gradient(135deg,#10b981,#059669)' }}
                        className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 sm:px-8 py-2.5 rounded-xl text-white text-[13px] font-bold shadow-lg transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSubmitting ? (
                          <><ArrowPathIcon className="w-4 h-4 animate-spin" /> Processing...</>
                        ) : isDebit ? (
                          <><ArrowDownTrayIcon className="w-4 h-4" /> Save Debit Transaction</>
                        ) : (
                          <><ArrowUpTrayIcon className="w-4 h-4" /> Save Credit Transaction</>
                        )}
                      </motion.button>

                      {userRole !== 'company_owner' && (
                        <p className="text-[11px] text-slate-500 flex items-center gap-1">
                          <ExclamationTriangleIcon className="w-3.5 h-3.5 text-amber-400" />
                          Only company owners can create credit transactions
                        </p>
                      )}
                    </div>
                  </div>
                </form>
              )}
            </div>

            {/* Recent transactions */}
            <div className="mt-6">
              <CreateTransactionReport onRefresh={refreshTrigger} />
            </div>
          </div>
        </main>
      </div>
      <ToastContainer position="top-right" autoClose={3000} theme="light" />
    </div>
  );
};

export default TransactionForm;
